import { CommonModule } from '@angular/common';
import { Component, computed, input, OnInit, output, signal } from '@angular/core';

import { Location } from '@sondermind/utilities/models-location';

import { SessionType, SessionTypeDisplay } from '@sondermind/utilities/models-session';
import {
  IAppointmentSessionTypeSelection,
  ISessionTypeOption
} from '../../models/appointment-session-type-select.interface';
import { FormatAddressPipe } from '../../pipes/format-address.pipe';

@Component({
  selector: 'appointment-session-type-select',
  standalone: true,
  imports: [CommonModule, FormatAddressPipe],
  templateUrl: './appointment-session-type-select.component.html',
  styleUrls: ['./appointment-session-type-select.component.scss']
})
export class AppointmentSessionTypeSelectComponent implements OnInit {
  readonly locations = input.required<Location[]>();
  readonly sessionTypesOffered = input.required<string[]>();
  readonly currentSelection = input<string>('');

  readonly sessionTypeChange = output<IAppointmentSessionTypeSelection>();

  readonly selectedValue = signal<string>('');

  readonly radioGroupName = 'appointment-session-type-select';

  readonly options = computed<ISessionTypeOption[]>(() => this.buildOptions(this.locations()));

  ngOnInit(): void {
    this.buildOptions(this.locations());
    this.syncOptionsFromInputs();
  }

  getOptionId(option: ISessionTypeOption): string {
    return `appointment-session-type-${option.value}`;
  }

  onCardClick(option: ISessionTypeOption): void {
    this.onSelectionChange(option.value);
  }

  onSelectionChange(value: string): void {
    this.selectedValue.set(value);
    this.emitSelection(value);
  }

  private syncOptionsFromInputs(): void {
    if (this.options().length) {
      let initialValue;
      if (this.currentSelection().length) {
        initialValue = this.options().find((opt) => opt.value === this.currentSelection());
      }

      if (initialValue) {
        this.selectedValue.set(initialValue.value);
      } else {
        this.selectedValue.set(this.options()[0].value);
        this.emitSelection(this.options()[0].value);
      }
    }
  }

  private buildOptions(locations: Location[]): ISessionTypeOption[] {
    const types = this.sessionTypesOffered()?.map((t) => t.toLowerCase()) ?? [];

    const opts: ISessionTypeOption[] = [];
    const hasVirtual = types.some(
      (t) => t === SessionType.VIDEO
    );
    const hasInPerson = types.some((t) => t === SessionType.IN_PERSON);

    if (hasVirtual) {
      opts.push({ label: 'Virtual', value: SessionType.VIDEO });
    }

    if (hasInPerson && locations?.length) {
      locations.forEach((location) => {
        opts.push({
          label: SessionTypeDisplay.IN_PERSON,
          value: location.id.toString(),
          location
        });
      });
    }

    return opts;
  }

  private emitSelection(value: string): void {
    if (value === SessionType.VIDEO) {
      this.sessionTypeChange.emit({ sessionType: SessionType.VIDEO, locationId: null });
    } else {
      const locationId = Number(value);
      if (!Number.isNaN(locationId)) {
        this.sessionTypeChange.emit({
          sessionType: SessionType.IN_PERSON,
          locationId
        });
      }
    }
  }
}
